import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { usePlatformAuth } from '../../context/PlatformAuthContext';
import PlatformLayout from '../../components/layout/PlatformLayout';
import PasswordSection from '../../components/PasswordSection';
import AuthenticatorSection from './AuthenticatorSection';
import { isOwner } from '../../lib/staffRoles';
import './Platform.css';
import { Building2, Users, Zap, TrendingUp, Plus, Mail } from 'lucide-react';

export default function PlatformDashboard() {
  const navigate = useNavigate();
  const { platformAxios, adoptToken, platformUser } = usePlatformAuth();

  const { data: analytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ['platform-analytics'],
    queryFn: () => platformAxios.get('/analytics').then(res => res.data)
  });

  const { data: clubs, isLoading: clubsLoading } = useQuery({
    queryKey: ['platform-clubs'],
    queryFn: () => platformAxios.get('/clubs').then(res => res.data)
  });

  const recentClubs = clubs?.slice(0, 5) || [];

  return (
    <PlatformLayout>
      <div className="platform-page">
        <div className="page-header">
          <h1>Dashboard</h1>
          <p>Platform Overview & Quick Actions</p>
        </div>

        {/* Analytics Cards — each clicks through to its SAP section (24 Aug) */}
        <div className="analytics-grid">
          <div className="analytics-card clickable" role="button" tabIndex={0}
            onClick={() => navigate('/marakin-admin/clubs')}
            onKeyDown={e => e.key === 'Enter' && navigate('/marakin-admin/clubs')}>
            <div className="card-icon building">
              <Building2 size={28} />
            </div>
            <div className="card-content">
              <p className="card-label">Total Clubs</p>
              <p className="card-value">
                {analyticsLoading ? '—' : analytics?.totalClubs || 0}
              </p>
            </div>
          </div>

          <div className="analytics-card clickable" role="button" tabIndex={0}
            onClick={() => navigate('/marakin-admin/clubs')}
            onKeyDown={e => e.key === 'Enter' && navigate('/marakin-admin/clubs')}>
            <div className="card-icon users">
              <Users size={28} />
            </div>
            <div className="card-content">
              <p className="card-label">Total Members</p>
              <p className="card-value">
                {analyticsLoading ? '—' : analytics?.totalMembers || 0}
              </p>
            </div>
          </div>

          <div className="analytics-card clickable" role="button" tabIndex={0}
            onClick={() => navigate('/marakin-admin/sponsors')}
            onKeyDown={e => e.key === 'Enter' && navigate('/marakin-admin/sponsors')}>
            <div className="card-icon sponsors">
              <Zap size={28} />
            </div>
            <div className="card-content">
              <p className="card-label">Total Sponsors</p>
              <p className="card-value">
                {analyticsLoading ? '—' : analytics?.totalSponsors || 0}
              </p>
            </div>
          </div>

          <div className="analytics-card clickable" role="button" tabIndex={0}
            onClick={() => navigate('/marakin-admin/reports')}
            onKeyDown={e => e.key === 'Enter' && navigate('/marakin-admin/reports')}>
            <div className="card-icon redemptions">
              <TrendingUp size={28} />
            </div>
            <div className="card-content">
              <p className="card-label">Total Redemptions</p>
              <p className="card-value">
                {analyticsLoading ? '—' : analytics?.totalRedemptions || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="quick-actions">
          <h2>Quick Actions</h2>
          <div className="actions-grid">
            <button
              className="action-button"
              onClick={() => navigate('/marakin-admin/clubs')}
            >
              <div className="action-icon">
                <Plus size={20} />
              </div>
              <div className="action-content">
                <p className="action-title">Create Club</p>
                <p className="action-desc">Add a new club to the platform</p>
              </div>
            </button>

            <button
              className="action-button"
              onClick={() => navigate('/marakin-admin/clubs')}
            >
              <div className="action-icon">
                <Mail size={20} />
              </div>
              <div className="action-content">
                <p className="action-title">Invite Admin</p>
                <p className="action-desc">Send club admin invitations</p>
              </div>
            </button>
          </div>
        </div>

        {/* Recent Clubs */}
        <div className="recent-section">
          <div className="section-header">
            <h2>Recent Clubs</h2>
            <button
              className="btn-text"
              onClick={() => navigate('/marakin-admin/clubs')}
            >
              View All
            </button>
          </div>

          {clubsLoading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading clubs...</p>
            </div>
          ) : recentClubs.length > 0 ? (
            <div className="clubs-list">
              {recentClubs.map((club) => (
                <div key={club.id} className="club-item clickable" role="button" tabIndex={0}
                  title={`Open ${club.name} in Clubs`}
                  onClick={() => navigate(`/marakin-admin/clubs?club=${club.id}`)}
                  onKeyDown={e => e.key === 'Enter' && navigate(`/marakin-admin/clubs?club=${club.id}`)}>
                  <div className="club-info">
                    <h3>{club.name}</h3>
                    <p>{club.sport} • {club.location}</p>
                  </div>
                  <div className="club-stats">
                    <span className="member-count">{club.member_count} members</span>
                    <span className={`status-badge ${club.status}`}>
                      {club.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <Building2 size={48} />
              <h3>No clubs yet</h3>
              <p>Create your first club to get started</p>
              <button
                className="btn-primary"
                onClick={() => navigate('/marakin-admin/clubs')}
              >
                Create Club
              </button>
            </div>
          )}
        </div>

        {/* B-65 addition: the staff portal has no profile page, so Change password lives here */}
        <div className="dashboard-section" style={{ marginTop: 32, maxWidth: 520 }}>
          <h2>Your password</h2>
          <PasswordSection onNewToken={adoptToken} />
        </div>

        {/* v53: the Owner's authenticator — needed only to set a member's password */}
        {isOwner(platformUser?.role) && (
          <div className="dashboard-section" style={{ marginTop: 32, maxWidth: 520 }}>
            <h2>Your authenticator</h2>
            <AuthenticatorSection />
          </div>
        )}
      </div>
    </PlatformLayout>
  );
}
